import React, { useEffect, useState } from "react";
import {
  Button,
  CreateProps,
  SimpleForm,
  useDataProvider,
  useRedirect,
} from "react-admin";
import {
  Grid,
  Step,
  Stepper,
  StepLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Typography,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import { useStyles } from "./style";
import CreateWrapper from "../../components/CreateWrapper";
import SelectWaveAndEffectiveDateSection from "./components/SelectWaveAndEffectiveDateSection";
import AdjustStudentAndAddressSection from "./components/AdjustStudentAndAddressSection";
import OptimiseRouteSection from "./components/OptimiseRouteSection";
import FinalPreviewSection from "./components/FinalPreviewSection";
import RoutePlanMap from "./components/RoutePlanMap";
import { useDispatch } from "react-redux";
import { changeSelectedRoute } from "../../redux/navmode.action";
import StepperNavigation from "./components/StepperNavigation";
import SanitizedGrid from "../../components/SanitizedGrid";
import Alert from "@material-ui/lab/Alert";
import TableResultRouteCreation, {
  IRecord,
} from "./components/TableResultRouteCreation";
import { find, findIndex } from "lodash";
import { processRoutesToRemoveSchoolLocation } from "./tools";
import CustomSplit from "../../components/CustomSplit";
import LoadingProgressComponent from "./components/LoadingProgressComponent";

const EnhancedRoutePlanCreate = (props: CreateProps) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);

  const [recordToSave, setRecordToSave] = useState<any>(null);
  const [busSchedulePlan, setBusSchedulePlan] =
    useState<IBusSchedulePlans>(null);
  const [results, setResults] = useState<IRecord[]>([]);
  const [processingRoute, setProcessingRoute] = useState<boolean>(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] =
    useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [isLoadingMap, setIsLoadingMap] = useState<boolean>(false);
  const [progressMap, setProgressMap] = useState(1);

  const dispatch = useDispatch();
  const provider = useDataProvider();
  const redirect = useRedirect();

  const breadcrumb = [
    { link: "/enhancedBusSchedulePlans", title: "AI Route Plans" },
    { link: "", title: "New AI Route Plan" },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setProcessingRoute(false);
  };

  const handleFinish = () => {
    redirect("/enhancedBusSchedulePlans");
  };

  const isContainsProcessedData = (results: IRecord[]) => {
    return results.find(
      (result) =>
        result.recordStatus?.includes("Loading...") ||
        result.recordStatus?.includes("Retry"),
    );
  };

  const getContent = (activeStep: number) => {
    switch (activeStep) {
      case 0:
        return <SelectWaveAndEffectiveDateSection />;
      case 1:
        return <AdjustStudentAndAddressSection />;
      case 2:
        return <OptimiseRouteSection mode="create" progressMap={progressMap} />;
      case 3:
        return <FinalPreviewSection />;
      default:
        return <></>;
    }
  };

  const transformRoute = (route: any) => {
    return {
      id: route.realRouteId,
      name: route.routeName,
      startTime: route.startTime,
      endTime: route.endTime,
      driverId: route.driverId,
      busAssistantId: route.busAssistantId,
      vehicleId: route.vehicleId,
      backupDriverIds: route.backupDriverIds,
      backupBusAssistantIds: route.backupBusAssistantIds,
      backupVehicleIds: route.backupVehicleIds,
      assignedSubcontractor: route.assignedSubcontractor,
      routeGeometries: route.routeGeometries,
      routeLocations: route.routeLocations.map((loc: any) => {
        return {
          ...loc,
          id: loc.id,
          arrivedAt: loc.arrivalTime,
          studentIds: loc.students.map((student: any) => student.id),
        };
      }),
    };
  };

  const fetchCreateRoute = async (
    route: any,
    busSchedulePlanId: number,
    index: number,
    retryCount: number = 0,
  ) => {
    try {
      const data = transformRoute(route);

      await provider.customUpdate(
        `busSchedulePlans/${busSchedulePlanId}/routes/${data.id}`,
        {
          data: data,
        },
      );

      results[index].recordStatus = "Success";
      setResults([...results]);
    } catch (err: any) {
      retryCount++;

      const errorMessage = err.message;
      if (retryCount <= 5 && !errorMessage.includes("Success")) {
        results[index].recordStatus = `Retry... (${retryCount}/5)`;
        setResults([...results]);

        // give random delay between 0.5 - 1 seconds
        const delay = Math.floor(Math.random() * 1000) + 500;
        setTimeout(() => {
          fetchCreateRoute(route, busSchedulePlanId, index, retryCount);
        }, delay);

        return;
      }

      results[index].recordStatus = `Error, ${errorMessage}`;
      setResults([...results]);
    }
  };

  const handleCreateRoutes = async (
    routes: any[],
    busSchedulePlanId: number,
  ) => {
    for (const route of routes) {
      const index = routes.indexOf(route);

      results[index].recordStatus = "Loading...";

      setResults([...results]);

      await fetchCreateRoute(route, busSchedulePlanId, index);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaveButtonClicked(true);
      setLoadingSave(true);

      const { data: busSchedulePlan } = await provider.create(
        `busSchedulePlans`,
        {
          data: { ...recordToSave, isCreatedByRouteOptimization: true },
        },
      );

      if (recordToSave.routes.length > 0) {
        await handleCreateRoutes(
          recordToSave.routes,
          Number(busSchedulePlan.id),
        );
        setBusSchedulePlan(busSchedulePlan as IBusSchedulePlans);
      } else {
        setLoadingSave(false);
        handleFinish();
      }
    } catch (err: any) {
      results.forEach((result) => {
        result.recordStatus = "Error, failed create route plan";
      });

      setResults([...results]);
      setLoadingSave(false);
    }
  };

  const handleRetry = async (selectedRecord: any) => {
    if (selectedRecord) {
      const index = findIndex(results, {
        recordDescription: selectedRecord.recordDescription,
      });
      const findRecord = find(recordToSave.routes, {
        routeName: selectedRecord.recordDescription,
      });

      if (index < 0 || !findRecord) {
        return;
      }

      results[index].recordStatus = "Loading...";

      setResults([...results]);

      await fetchCreateRoute(findRecord, busSchedulePlan.id, index);
    }
  };

  const handleConfirmationSave = (values: any) => {
    handleOpenDialog();

    setRecordToSave({
      ...values,
      routes: processRoutesToRemoveSchoolLocation(
        values.routes,
        values.busSchedule.type,
      ),
    });
  };

  useEffect(() => {
    dispatch(changeSelectedRoute(null));
  }, []);

  useEffect(() => {
    if (activeStep === 3 && recordToSave?.routes.length > 0) {
      const tempResults = recordToSave.routes.map((record: any) => {
        return {
          recordDescription: record.routeName,
          recordStatus: "Pending",
        };
      });
      setResults(tempResults);
    }
  }, [recordToSave, activeStep]);

  useEffect(() => {
    if (isContainsProcessedData(results)) {
      setProcessingRoute(true);
    } else {
      setProcessingRoute(false);
    }
  }, [results]);

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New AI Route Plan"
        isHaveSchoolFilter
        {...props}
      >
        <SimpleForm
          className={classes.simpleForm}
          initialValues={{
            formOptimizationConstraintsChanged: false,
            formStudentsChanged: false,
            formRoutesChanged: false,
            students: [],
            routes: [],
            droppedLocations: [],
          }}
          toolbar={null}
          save={handleConfirmationSave}
        >
          <CustomSplit
            sizes={[50, 50]}
            minSize={[700, 300]}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
          >
            <div style={{ padding: "24px 34px" }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                <Step>
                  <StepLabel>Select Wave and Effective Date</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Adjust the Student and Address</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Generate Routes</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Final Review</StepLabel>
                </Step>
              </Stepper>

              <StepperNavigation
                activeStep={activeStep}
                handleNext={handleNext}
                handleBack={handleBack}
                mode="create"
              />

              <div style={{ padding: "12px 14px" }}>
                {getContent(activeStep)}
              </div>
            </div>
            <div style={{ padding: 0 }}>
              <RoutePlanMap
                mode="create"
                setIsLoadingMap={setIsLoadingMap}
                setProgressMap={setProgressMap}
              />
            </div>
          </CustomSplit>

          <Backdrop className={classes.backdropLoading} open={isLoadingMap}>
            <LoadingProgressComponent value={progressMap} />
          </Backdrop>
        </SimpleForm>
      </CreateWrapper>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Route Plan Saving Progress
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            {isSaveButtonClicked && recordToSave.routes.length > 0 ? (
              <>
                <Grid item xs={12}>
                  <Alert severity="warning" style={{ margin: "0 16px" }}>
                    Please wait for the process to complete, do not close the
                    page.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TableResultRouteCreation
                    records={results}
                    handleRetry={handleRetry}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1">
                  Are you sure to save the route plan?
                </Typography>
              </Grid>
            )}
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          {isSaveButtonClicked && recordToSave.routes.length > 0 ? (
            <Button
              onClick={handleFinish}
              color="primary"
              label="Done"
              disabled={processingRoute}
            />
          ) : (
            <>
              <Button
                onClick={handleCloseDialog}
                color="secondary"
                label="Back"
              />
              <Button onClick={handleSave} color="primary" label="Save">
                {loadingSave && (
                  <CircularProgress
                    style={{ color: "#FFFFFF", width: "20px", height: "20px" }}
                  />
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnhancedRoutePlanCreate;
